import React, { useState, useRef } from 'react';
import useApi from '../../hooks/useApi';
import { Application } from '../../types';

interface AddAppFormProps {
  onAppAdded: (newApp: Application) => void;
}

const AddAppForm: React.FC<AddAppFormProps> = ({ onAppAdded }) => {
  const [name, setName] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const { securedRequest, loading } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // The required attribute on inputs handles this, but this is good defense.
    if (!name || !url || !logoFile) {
      alert('Please fill all fields and select a logo file.');
      return;
    }

    // Step 1: Create the application with name and url
    const appData = { name, url };
    const { data: newApp, error: creationError } = await securedRequest(
      'post',
      '/api/applications',
      appData
    );

    if (creationError) {
      alert('Failed to create application.');
      console.error(creationError);
      return;
    }

    if (newApp) {
      // Step 2: Upload the logo to the new endpoint
      const logoFormData = new FormData();
      logoFormData.append('logoFile', logoFile);
      const { data: updatedApp, error: uploadError } = await securedRequest(
        'put',
        `/api/applications/${newApp.id}/logo`,
        logoFormData
      );

      if (uploadError) {
        // Note: You might want to handle this case more gracefully,
        // as the application has been created but the logo upload failed.
        alert('Application was created, but failed to upload logo.');
        console.error(uploadError);
      }

      onAppAdded(updatedApp || newApp);
      setName('');
      setUrl('');
      setLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="card my-4">
      <div className="card-body">
        <h5 className="card-title">Admin: Add New Application</h5>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-2">
              <input type="text" className="form-control" placeholder="App Name" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} required />
            </div>
            <div className="col-md-6 mb-2">
              <input type="url" className="form-control" placeholder="App URL" value={url} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)} required />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-md-11 mb-2">
              <input type="file" className="form-control" ref={fileInputRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLogoFile(e.target.files ? e.target.files[0] : null)} required />
            </div>
            <div className="col-md-1 mb-2 d-grid">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? '...' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAppForm;
